import { Button } from "@/components/ui/button";
import { ArtworkCard } from "@/components/ArtworkCard";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { ArrowRight, Search } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProductCardSkeleton } from "@/components/LoadingSpinner";
import { combineStructuredData, getOrganizationData, getWebSiteData, getItemListData } from "@/lib/structuredData";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthProvider";
import { useState } from "react";

const CATEGORIES = [
  { key: "painting", value: "Måleri" },
  { key: "photography", value: "Fotografi" },
  { key: "illustration", value: "Illustration" },
  { key: "sculpture", value: "Skulptur" },
  { key: "digital", value: "Digital konst" },
];

export default function Home() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: artworks
  } = useQuery({
    queryKey: ["featured-artworks"],
    queryFn: async () => {
      const {
        data: arts,
        error
      } = await supabase.from("artworks").select(`
          *,
          artwork_images (image_url)
        `).eq("is_active", true).eq("approval_status", "approved").order("created_at", {
        ascending: false
      }).limit(12);
      if (error) throw error;

      const artistIds = Array.from(new Set((arts || []).map((a: any) => a.artist_id).filter(Boolean)));
      let profilesMap = new Map<string, any>();
      if (artistIds.length > 0) {
        const { data: profiles } = await supabase
          .from("public_profiles")
          .select("id, username, full_name")
          .in("id", artistIds);
        (profiles || []).forEach((p: any) => profilesMap.set(p.id, p));
      }

      const enriched = (arts || []).map((a: any) => ({
        ...a,
        public_profiles: profilesMap.get(a.artist_id) || null,
      }));

      return enriched;
    }
  });

  const structuredData = combineStructuredData(
    getOrganizationData(),
    getWebSiteData(),
    ...(artworks && artworks.length > 0 
      ? [getItemListData(artworks.map((a: any) => ({
          id: a.id,
          title: a.title,
          description: a.description,
          price: a.price,
          image_url: a.artwork_images?.[0]?.image_url,
          artist_name: a.public_profiles?.full_name || a.public_profiles?.username
        })), t('home.featured.title'))] 
      : [])
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/explore?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate("/explore");
    }
  };

  return <div className="min-h-screen bg-background">
      <SEOHead 
        title={t('home.seo.title')} 
        description={t('home.seo.description')} 
        canonicalUrl="/" 
        keywords={["konst", "konstverk", "original", "tryck", "svenska konstnärer", "köp konst online"]} 
        structuredData={structuredData} 
      />
      <Navbar />
      
      {/* Compact Hero with Search */}
      <section className="relative overflow-hidden bg-gradient-to-br from-background via-muted/30 to-accent/20 pt-8 pb-6 sm:pt-12 sm:pb-8 md:pt-16 md:pb-10 border-b border-border/50">
        <div className="container relative z-10 px-6 sm:px-4">
          <div className="mx-auto max-w-3xl text-center animate-fade-in">
            <h1 className="mb-3 sm:mb-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold tracking-tight leading-[1.1]">
              {t('home.hero.title')}{" "}
              <span className="relative inline-block">
                <span className="relative z-10">{t('home.hero.titleHighlight')}</span>
                <span className="absolute inset-0 bg-gradient-to-r from-accent/30 via-accent/20 to-accent/30 blur-xl"></span>
              </span>
            </h1>
            <p className="mb-5 sm:mb-6 text-sm sm:text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto">
              {t('home.hero.description')}
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mx-auto max-w-xl mb-5 sm:mb-6">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-foreground transition-colors" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('home.search.placeholder')}
                  className="w-full h-12 sm:h-14 pl-12 pr-4 rounded-2xl text-base bg-[hsl(var(--glass-bg))] backdrop-blur-md border border-[hsl(var(--glass-border-strong))] shadow-[inset_0_1px_0_hsl(var(--glass-highlight)),inset_0_-1px_2px_hsl(var(--glass-shadow))] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:bg-[hsl(var(--glass-bg-strong))] focus:border-[hsl(var(--glass-border))] transition-all duration-300"
                />
              </div>
            </form>

            {/* Category Chips */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              {CATEGORIES.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/explore?category=${encodeURIComponent(cat.value)}`}
                  className="inline-flex items-center rounded-full border border-border/60 bg-card/80 backdrop-blur-sm px-3.5 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-foreground hover:bg-accent/20 hover:border-accent/40 transition-all duration-200 shadow-sm"
                >
                  {t(`home.categories.${cat.key}`)}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-8 sm:py-10 md:py-14 lg:py-16">
        <div className="container px-6 sm:px-4">
          <div className="mb-5 sm:mb-6 md:mb-8 flex items-baseline justify-between">
            <h2 className="text-lg sm:text-xl md:text-2xl font-serif font-bold">{t('home.featured.title')}</h2>
            <Link to="/explore" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
              {t('home.featured.viewAll')}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid gap-3 sm:gap-4 md:gap-5 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {!artworks ?
              Array.from({ length: 12 }).map((_, i) => <ProductCardSkeleton key={i} />) 
              : artworks.length > 0 ? artworks.map(artwork => <ArtworkCard 
                key={artwork.id} 
                id={artwork.id} 
                title={artwork.title} 
                artistName={(artwork.public_profiles as any)?.username || artwork.public_profiles?.full_name || t('common.unknownArtist')} 
                price={Number(artwork.price)} 
                imageUrl={artwork.artwork_images?.[0]?.image_url || "/placeholder.svg"} 
                category={artwork.category} 
                artworkType={artwork.artwork_type} 
                stockQuantity={artwork.stock_quantity} 
              />) 
              : <div className="col-span-full text-center py-12">
                  <p className="text-muted-foreground">{t('home.featured.empty')}</p>
                </div>
            }
          </div>
        </div>
      </section>

      {/* Newsletter Section - Only for logged out users */}
      {!user && (
        <section className="py-10 sm:py-12 md:py-16 bg-muted/30 border-b border-border/50">
          <div className="container px-6 sm:px-4">
            <NewsletterSignup />
          </div>
        </section>
      )}

      {/* Compact CTA Section */}
      <section className="py-8 sm:py-10 md:py-12 bg-primary text-primary-foreground relative overflow-hidden border-y border-border/50">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="container relative px-6 sm:px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-2 sm:mb-3 text-xl sm:text-2xl md:text-3xl font-serif font-bold">{t('home.cta.title')}</h2>
            <p className="mb-4 sm:mb-6 text-sm md:text-base leading-relaxed opacity-90 max-w-lg mx-auto">
              {t('home.cta.description')}
            </p>
            <Link to="/auth" className="inline-block">
              <Button size="lg" variant="secondary" className="text-sm sm:text-base font-medium shadow-premium h-10 sm:h-11">
                {t('home.cta.button')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>;
}
